import { useEffect, useState } from "react";
import { Portal } from "react-portal";
import { ChatKit, useChatKit } from '@openai/chatkit-react';
import { Button } from "zudoku/components";
import { BotIcon } from "zudoku/icons";
import clsx from "clsx";

const AiChatDrawer = () => {
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [isDark, setIsDark] = useState(false);
    const [isLargeScreen, setIsLargeScreen] = useState(true);
    const [isMounted, setIsMounted] = useState(false);
    const drawerBreakpoint = 1450;

    useEffect(() => {
        // Need this mounted check to prevent hydration errors
        // when using React Portal
        setIsMounted(true);
    }, []);

    useEffect(() => {
        const htmlElement = document.documentElement;
        const updateTheme = () => {
            setIsDark(htmlElement.classList.contains('dark'));
        };

        updateTheme();

        const observer = new MutationObserver(updateTheme);
        observer.observe(htmlElement, {
            attributes: true,
            attributeFilter: ['class']
        });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const checkScreenSize = () => {
            setIsLargeScreen(window.innerWidth >= drawerBreakpoint);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);

        return () => window.removeEventListener('resize', checkScreenSize);
    }, [drawerBreakpoint]);

    const { control } = useChatKit({
        theme: {
            colorScheme: isDark ? "dark" : "light",
            radius: "soft",
            typography: {
                fontFamily: "Scta Grotesk A, Open Sans, sans-serif"
            }
        },
        header: {
            leftAction: {
                icon: "collapse-right",
                onClick: () => setSidebarVisible(() => !sidebarVisible),
            },
        },
        composer: {
            placeholder: "Start asking me questions...",
        },
        disclaimer: {
            text: "",
        },
        startScreen: {
            greeting: "Let me help you with your Open Payments integration",
        },
        api: {
            async getClientSecret() {
                const apiHost = import.meta.env.ZUDOKU_PUBLIC_CHATKIT_API_HOST;

                let userId = localStorage.getItem('chatkit-user-id');
                if (!userId) {
                    userId = crypto.randomUUID();
                    localStorage.setItem('chatkit-user-id', userId);
                }

                const res = await fetch(
                    `${apiHost}/api/chatkit/session`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ user: userId }),
                    }
                );
                const { client_secret } = await res.json();
                return client_secret;
            },
        },
    });

    useEffect(() => {
        const root = document.getElementById("root");
        if (root) {
            root.style.transition = "width 300ms cubic-bezier(0, 0, 0.2, 1)";

            // On large screens, push content left. On small screens, overlay.
            if (isLargeScreen && sidebarVisible) {
                root.style.width = "calc(100% - 500px)";
            } else {
                root.style.width = "100%";
            }
        }
    }, [sidebarVisible, isLargeScreen]);

    return (
        <div className="">
            <Button
                onClick={() => setSidebarVisible(!sidebarVisible)}
                className="items-center gap-1 justify-center"
            >
                <BotIcon size={18} className="mb-[3px]" />
                <span>
                    {sidebarVisible ? "Close AI" : "Ask our AI"}
                </span>
            </Button>

            {isMounted && (
                // Portal places it's children in the footer of the DOM.
                // This is useful since this component is placed in the 
                // top navigation which messes up the positioning of the drawer.
                <Portal>
                    <div
                        className={clsx(
                            "fixed right-0 top-0 w-[500px] max-w-full h-dvh z-50",
                            "bg-slate-50 transition ease-out duration-300",
                            "shadow-lg",
                            !sidebarVisible && "translate-x-full"
                        )}
                    >
                        <ChatKit
                            control={control}
                            className="w-full h-full"
                        />
                    </div>

                    {/*
                        Backdrop overlay - only show on small
                        screens when drawer is open
                    */}
                    {!isLargeScreen && sidebarVisible && (
                        <div
                            className={clsx(
                                "fixed inset-0 bg-black/50 z-40",
                                "transition-opacity duration-300"
                            )}
                            onClick={() => setSidebarVisible(false)}
                        />
                    )}
                </Portal>
            )}
        </div>
    );
};

export default AiChatDrawer;